import "../JSONTools/JSONformatter.css"
import ToolHeader from "../../Components/ToolHeader";
import { useState } from "react";
import "./HMACCalculator.css";
import { Row, Col } from "react-bootstrap";

import { useTheme } from "../../Context/ThemeContext";
import { ReactComponent as CopyIcon } from "../../Icons/Copy.svg";
import { HMACGenerate, HMACVerify } from "../../Services/APIService";


const algorithms = [{ "name": "SHA-1", "value": "sha1" },
{ "name": "SHA-256", "value": "sha256" },
{ "name": "SHA-384", "value": "sha384" },
{ "name": "SHA-512", "value": "sha512" }];


const HMACCalculator = () => {
    const { theme } = useTheme();
    const [message, setMessage] = useState("");
    const [secretKey, setSecretKey] = useState("");
    const [generateAlgo, setGenerateAlgo] = useState({ "name": "SHA-1", "value": "sha1" });
    const [outputFormat, setOutputFormat] = useState("hex");
    const [generatedMac, setGeneratedMac] = useState("");
    const [verifyText, setVerifyText] = useState("");
    const [verifyKey, setVerifyKey] = useState("");
    const [verifyAlgo, setVerifyAlgo] = useState({ "name": "SHA-1", "value": "sha1" });
    const [providedMac, setProvidedMac] = useState("");
    const [verifyResult, setVerifyResult] = useState(null);
    const [hasCopied, setHasCopied] = useState(false);
    const toolFav = { toolName: 'HMAC Calculator', Iconpath: "/FileCodeRaw.svg" };


    

    

    const handleGenerate = async () => {
        if (!message || !secretKey) {
            setGeneratedMac("Please enter message and secret key.");
            return;
        }
        try {
            const payload = {
                message: message,
                secretKey: secretKey,
                algorithm: generateAlgo.value,
                outputFormat: outputFormat
            };
            const response = await HMACGenerate(payload);
            setGeneratedMac(response.data.hmac);
        } catch (error) {
            setGeneratedMac("Error generating HMAC: " + error.message);
        }
    };

    const handleVerify = async () => {
        if (!verifyText || !verifyKey || !providedMac) {
            setVerifyResult({ valid: false, message: "Fill all verify fields first." });
            return;
        }

        try {
            const payload = {
                message: verifyText,
                secretKey: verifyKey,
                algorithm: verifyAlgo.value,
                providedMac: providedMac,
                encoding: outputFormat
            };
            const signature = await HMACVerify(payload);

            setVerifyResult({
                valid: signature.data.valid,
                message: signature.data.valid ? "MAC verified successfully." : "MAC Mismatch"
            });
        } catch (error) {
            setVerifyResult({ valid: false, message: "Error verifying HMAC: " + error.message });
        }
    };

    const copyToClipboard = async () => {
        if (!generatedMac) return;
        await navigator.clipboard.writeText(generatedMac);
        setHasCopied(true);
        setTimeout(() => setHasCopied(false), 2000);
    };

    return (<>
        <Col>
            <ToolHeader title="HMAC Calculator" description="Generate and verify HMAC values using standard SHA algorithms." toolFav={toolFav} />
            <Row className="hmac-grid">
                <Col >
                    <div className="hmac-card-header">
                        <span>Generate HMAC</span>
                    </div>
                    <div className="hmac-card">
                        <label>Enter Plain Text to Compute Hash</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Enter the Message..."
                        />

                        <label>Enter the Secret Key</label>
                        <textarea
                            value={secretKey}
                            onChange={(e) => setSecretKey(e.target.value)}
                            placeholder="Enter your secret key..."
                        />

                        <label>Select Cryptographic Hash Function&nbsp;
                            {renderInfoIcon("SHA-256 uses 32-bit words where SHA-512 uses 64-bit words. There are also truncated versions of each standard, known as SHA-224, SHA-384, SHA-512/224 and SHA-512/256.")}

                        </label>
                        <select value={generateAlgo?.value} onChange={(e) => setGenerateAlgo(algorithms.find(a => a.value === e.target.value))}>
                            {algorithms.map((algo) => (
                                <option key={algo.value} value={algo.value}>
                                    {algo.name}
                                </option>
                            ))}
                        </select>

                        <div className="hmac-format-row">
                            <button
                                type="button"
                                className={outputFormat === "hex" ? "active" : "secondary"}
                                onClick={() => setOutputFormat("hex")}
                            >
                                Hex
                            </button>
                            <button
                                type="button"
                                className={outputFormat === "base64" ? "active" : "secondary"}
                                onClick={() => setOutputFormat("base64")}
                            >
                                Base64
                            </button>
                        </div>

                        <button className="hmac-submit" onClick={handleGenerate}>
                            Generate MAC
                        </button>
                        <div className="hmac-result-row">
                            <label>HMAC Result</label>
                            <div style={{display:"flex", alignItems:"center", gap:"5px"}}>
                                <CopyIcon className="headerIcon" onClick={copyToClipboard} /> 
                               {hasCopied ? "Copied!" : "Copy"}
                            </div>
                        </div>
                        <div className="hmac-result-box">{generatedMac}</div>
                    </div>
                </Col>

                <Col >
                    <div className="hmac-card-header">
                        <span>Verify HMAC</span>
                    </div>
                    <div className="hmac-card">
                        <label>Enter Plain Text Used for HMAC &nbsp;
                            {renderInfoIcon("Verification recomputes the HMAC using the provided message and secret, then compares it securely with the received MAC.")}
                        </label>
                        <textarea
                            value={verifyText}
                            onChange={(e) => setVerifyText(e.target.value)}
                            placeholder="Enter the Original message..."
                        />

                        <label>Enter the Secret Key &nbsp;
                            {renderInfoIcon("The same shared secret key used during HMAC generation.")}
                        </label>
                        <input
                            type="password"
                            value={verifyKey}
                            onChange={(e) => setVerifyKey(e.target.value)}
                            placeholder="Enter your secret key..."
                        />

                        <label>Select Cryptographic Hash Function
                        </label>
                        <select value={verifyAlgo?.value} onChange={(e) => setVerifyAlgo(algorithms.find(a => a.value === e.target.value))}>
                            {algorithms.map((algo) => (
                                <option key={algo.value} value={algo.value}>
                                    {algo.name}
                                </option>
                            ))}
                        </select>

                        <label>Enter Provided MAC &nbsp;
                            {renderInfoIcon("MAC received from API,webhook, or client.")}
                        </label>
                        <textarea
                            value={providedMac}
                            onChange={(e) => setProvidedMac(e.target.value)}
                            placeholder="Paste the MAC"
                        />

                        <button className="hmac-submit" onClick={handleVerify}>
                            Verify MAC
                        </button>

                        <div className={`verify-status ${verifyResult?.valid ? "valid" : "invalid"}`}>
                            {verifyResult ? verifyResult.message :
                                <div>
                                    <span style={{ color: theme === "dark" ? "#FFFFFF99" : "#00000099" }}>Enter details and click verify to check HMAC validity</span>
                                </div>}
                        </div>
                    </div>
                </Col>
            </Row>
        </Col>
    </>
    );
};

const renderInfoIcon = (tooltipText) => (
    <span className="info-tooltip-wrapper">
        <button
            type="button"
            className="info-icon"
        >
            i
        </button>
        <span className="bcrypt-tooltip">{tooltipText}</span>
    </span>
);

export default HMACCalculator;
